// FASE 3.1 — Subscribe-to-analyst button (on profile pages).
// Only renders when the analyst has Connect ready + price set. Redirects to Stripe Checkout.
import React, { useCallback, useEffect, useState } from "react";
import { Crown, Loader2 } from "lucide-react";
import { API, safeJson } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useLanguage } from "../../i18n/LanguageContext";
import { tFases } from "../../i18n/shadow-fases";
import { SubscribeWithRBL } from "./SubscribeWithRBL";

export const SubscribeButton = ({ username }) => {
  const { user, token, openLogin } = useAuth();
  const { lang } = useLanguage();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [alreadySubbed, setAlreadySubbed] = useState(false);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!username) return;
      try {
        const r = await fetch(`${API}/shadow/connect/analyst/${username}`);
        const d = await safeJson(r);
        if (alive && r.ok) setInfo(d);
      } catch (_e) { /* non-critical */ }
    };
    load();
    return () => { alive = false; };
  }, [username]);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      if (!user || !token) return;
      try {
        const r = await fetch(`${API}/shadow/connect/subscriptions`, {
          credentials: "include",
          headers: { Authorization: `Bearer ${token}` },
        });
        const d = await safeJson(r);
        if (alive && r.ok && Array.isArray(d?.subscriptions)) {
          setAlreadySubbed(d.subscriptions.some((s) => s.analyst_username === username && ["active", "trialing"].includes(s.status)));
        }
      } catch (_e) { /* non-critical */ }
    };
    load();
    return () => { alive = false; };
  }, [user, token, username]);

  const subscribe = useCallback(async () => {
    if (!user) { openLogin(); return; }
    if (busy) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/shadow/connect/subscribe/${username}`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json", "x-frontend-origin": window.location.origin, ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const d = await safeJson(r);
      if (!r.ok) throw new Error(d?.detail?.message || d?.detail || tFases(lang, "sub_failed"));
      if (d?.url) window.location.href = d.url;
    } catch (e) {
      // Optionally surface via toast — keep silent failure to a console warning
      console.warn("subscribe failed:", e.message);
      setBusy(false);
    }
  }, [user, token, busy, openLogin, username]);

  if (!info?.subscription_available) return null;
  if (alreadySubbed) {
    return (
      <span data-testid="profile-subscribed-badge" className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-400/[0.08] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.18em] text-emerald-200">
        <Crown className="h-3 w-3" /> {tFases(lang, "sub_subscribed")}
      </span>
    );
  }
  return (
    <div className="inline-flex flex-col gap-1" data-testid="subscribe-button-wrap">
      <button
        onClick={subscribe}
        disabled={busy}
        data-testid="profile-subscribe-btn"
        className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 px-4 py-2 text-xs font-black uppercase tracking-[0.15em] text-black hover:-translate-y-0.5 hover:shadow-[0_8px_22px_rgba(245,158,11,0.45)] disabled:opacity-60 transition-all"
      >
        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="h-3.5 w-3.5" />}
        €{info.subscription_price_eur}{tFases(lang, "sub_per_month")}
      </button>
      {/* R₿L parallel rail (renders only if price feed is live) */}
      <SubscribeWithRBL
        username={username}
        priceEur={info.subscription_price_eur}
      />
    </div>
  );
};

export default SubscribeButton;
